import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Users as UsersIcon, Eye } from 'lucide-react';
import apiService from '../../services/api';
import useToast from '../../hooks/useToast';
import { usePermissionContext } from '../../contexts/PermissionContext';
import ReactDOM from 'react-dom';
import ConfirmModal from '../ConfirmModal';
const UsersList = ({
    onView,
    currentPage = 1,
    setCurrentPage = () => { },
    itemsPerPage = 10,
    users = [],
    loading = false,
    error = null,
    onRefresh,
    filterRole = 'all',
    filterStatus = 'all',
    filterAllowLogin = 'all'
}) => {
    const navigate = useNavigate();
    const { showSuccess, showError } = useToast();
    const { hasPermission, permissions } = usePermissionContext();
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: null });
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, action: '', data: null });

    const handleEdit = async (user) => {
        if (permissions.length > 0 && !hasPermission('user.update')) {
            showError('You do not have permission to edit users');
            return;
        }
        // Navigate instantly using the list data
        navigate('/users/add', { state: { user } });
    };

    const handleDelete = async (user) => {
        if (permissions.length > 0 && !hasPermission('user.delete')) {
            showError('You do not have permission to delete users');
            return;
        }
        setOpenDropdownId(null);
        setConfirmModal({
            isOpen: true,
            action: 'delete',
            data: user
        });
    };

    const confirmAction = async () => {
        const { action, data } = confirmModal;
        setConfirmModal({ isOpen: false, action: '', data: null });

        if (action === 'delete') {
            try {
                const response = await apiService.deleteUser(data.id);
                if (response.success) {
                    showSuccess('User deleted successfully!');
                    if (onRefresh) onRefresh(); // Refresh the user list via parent
                } else {
                    showError(response.message || 'Failed to delete user');
                }
            } catch (error) {
      /* console.error('Error deleting user:', error); */
                showError(error.message || 'Failed to delete user');
            }
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedUsers = (usersToSort) => {
        let filtered = [...usersToSort];

        // Apply Role Filter
        if (filterRole !== 'all') {
            filtered = filtered.filter(u => {
                const roleName = u.role?.name || u.role_name || '';
                return roleName.toLowerCase() === filterRole.toLowerCase();
            });
        }

        // Apply Status Filter
        if (filterStatus !== 'all') {
            filtered = filtered.filter(u => {
                const isActive = u.status === 'active' || u.is_active === 1 || u.is_active === true;
                return filterStatus === 'active' ? isActive : !isActive;
            });
        }

        // Apply Allow Login Filter
        if (filterAllowLogin !== 'all') {
            filtered = filtered.filter(u => {
                const allowLogin = u.allow_login === 1 || u.allow_login === true || u.allow_login === '1';
                return filterAllowLogin === '1' ? allowLogin : !allowLogin;
            });
        }

        if (!sortConfig.key) return filtered;

        return filtered.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (aValue === null || aValue === undefined) aValue = '';
            if (bValue === null || bValue === undefined) bValue = '';

            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();

            if (!isNaN(parseFloat(aValue)) && !isNaN(parseFloat(bValue))) {
                aValue = parseFloat(aValue);
                bValue = parseFloat(bValue);
            }

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const toggleDropdown = (userId, event) => {
        event.stopPropagation();

        if (openDropdownId === userId) {
            setOpenDropdownId(null);
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        const dropdownHeight = 120; // Approximate height of dropdown
        const viewportHeight = window.innerHeight;

        let top = null;
        let bottom = null;

        if (rect.bottom + dropdownHeight <= viewportHeight) {
            top = rect.bottom + window.scrollY;
        } else {
            bottom = viewportHeight - rect.top + window.scrollY;
        }

        setDropdownPosition({
            top,
            bottom,
            left: rect.left + window.scrollX
        });

        setOpenDropdownId(userId);
    };

    useEffect(() => {
        const handleClickOutside = () => setOpenDropdownId(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Show loading state with uniform UI
    if (loading) {
        return (
            <div className="loading-sales">
                <h3>⏳ Loading Users...</h3>
                <p>Fetching users data from server...</p>
            </div>
        );
    }

    // Show error state with retry
    if (error) {
        return (
            <div className="no-sales" style={{ padding: '2rem', textAlign: 'center' }}>
                <h3>⚠️ Failed to Load Users</h3>
                <p style={{ color: '#64748b', marginBottom: '1rem' }}>{error}</p>
                {onRefresh && (
                    <button className="control-btn-primary" onClick={onRefresh}>
                        Retry
                    </button>
                )}
            </div>
        );
    }

    const sortedUsers = getSortedUsers(users);
    const pageVal = currentPage || 1;
    const perPageVal = itemsPerPage || 10;
    const totalItems = sortedUsers.length;
    const totalPages = Math.ceil(totalItems / perPageVal) || 1;
    const startIndex = (pageVal - 1) * perPageVal;
    const paginatedUsers = sortedUsers.slice(startIndex, startIndex + perPageVal);

    return (
        <div>
            <div className="table-responsive">
                <table className="products-table">
                <thead>
                    <tr>
                        <th style={{ width: '110px' }}>Action</th>
                        {[
                            { key: 'username', label: 'Username' },
                            { key: 'first_name', label: 'Full Name' },
                            { key: 'email', label: 'Email' },
                            { key: 'role', label: 'Role' },
                            { key: 'status', label: 'Status' }
                        ].map(({ key, label }) => (
                            <th key={key} className="sortable" onClick={() => handleSort(key)}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                                    {label}
                                    {sortConfig.key === key ? (
                                        sortConfig.direction === 'asc' ? (
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--etpos-primary)' }}>
                                                <path d="M12 19V5M5 12l7-7 7 7" />
                                            </svg>
                                        ) : (
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--etpos-primary)' }}>
                                                <path d="M12 5v14M5 12l7 7 7-7" />
                                            </svg>
                                        )
                                    ) : (
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M7 15l5 5 5-5" />
                                            <path d="M7 9l5-5 5 5" />
                                        </svg>
                                    )}
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {paginatedUsers.length === 0 ? (
                        <tr>
                            <td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                                No users found. Create your first user to get started.
                            </td>
                        </tr>
                    ) : (
                        paginatedUsers.map((user) => (
                            <tr key={user.id}>
                                <td className="actions-cell">
                                    <div className="action-dropdown-container">
                                        <button
                                            className="action-btn-trigger"
                                            onClick={(e) => toggleDropdown(user.id, e)}
                                        >
                                            Actions <span className="arrow-down">▼</span>
                                        </button>
                                        {openDropdownId === user.id && ReactDOM.createPortal(
                                            <div
                                                className="action-dropdown-menu"
                                                style={{
                                                    position: 'fixed',
                                                    top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                                    bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                                    left: `${dropdownPosition.left}px`,
                                                    zIndex: 1000
                                                }}
                                            >
                                                <button onClick={() => {
                                                    setOpenDropdownId(null);
                                                    onView && onView(user);
                                                }}>
                                                    👁️ View
                                                </button>
                                                {(permissions.length === 0 || hasPermission('user.update')) && (
                                                    <button onClick={() => handleEdit(user)}>
                                                        ✏️ Edit
                                                    </button>
                                                )}
                                                {(permissions.length === 0 || hasPermission('user.delete')) && (
                                                    <button onClick={() => handleDelete(user)}>
                                                        🗑️ Delete
                                                    </button>
                                                )}
                                            </div>,
                                            document.body
                                        )}
                                    </div>
                                </td>
                                <td>{user.username || user.email}</td>
                                <td>{user.first_name && user.last_name
                                    ? `${user.first_name} ${user.last_name}`
                                    : user.full_name || '-'}</td>
                                <td>{user.email || '-'}</td>
                                <td>{user.role?.name || user.role_name || '-'}</td>
                                <td>
                                    {user.status === 'active' || user.is_active ? (
                                        <span style={{
                                            padding: '0.25rem 0.5rem',
                                            backgroundColor: '#dcfce7',
                                            color: '#166534',
                                            borderRadius: '4px',
                                            fontSize: '0.75rem',
                                            fontWeight: '500'
                                        }}>
                                            Active
                                        </span>
                                    ) : (
                                        <span style={{
                                            padding: '0.25rem 0.5rem',
                                            backgroundColor: '#fee2e2',
                                            color: '#991b1b',
                                            borderRadius: '4px',
                                            fontSize: '0.75rem',
                                            fontWeight: '500'
                                        }}>
                                            Inactive
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
            </div>

            {totalItems > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', marginTop: '0.5rem', borderTop: '1px solid #e2e8f0' }}>
                    <div style={{ color: '#334155', fontSize: '0.9rem', fontWeight: '500' }}>
                        Showing {startIndex + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', color: '#334155', fontWeight: '500', fontSize: '0.95rem' }}>
                            Page {currentPage} of {totalPages || 1}
                        </span>
                        <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.action === 'delete' ? 'Delete User' : 'Confirm Action'}
                message={confirmModal.action === 'delete' ? `Are you sure you want to delete user "${confirmModal.data?.username || confirmModal.data?.email}"?` : 'Are you sure?'}
                onConfirm={confirmAction}
                onCancel={() => setConfirmModal({ isOpen: false, action: '', data: null })}
                confirmText={confirmModal.action === 'delete' ? 'Delete' : 'Confirm'}
            />
        </div>
    );
};

export default UsersList;
